import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { 
  HiOutlineChartBar, 
  HiOutlineMagnifyingGlass, 
  HiOutlineCpuChip, 
  HiOutlineCheckCircle, 
  HiOutlineArrowRight, 
  HiOutlineBars3, 
  HiOutlineXMark 
} from 'react-icons/hi2';
import StickyParallaxSection from './components/StickyParallaxSection';
import TestimonialSwiper from './components/TestimonialSwiper';
import PricingSection from './components/PricingSection';
import FeaturesSection from './components/FeaturesSection';

// Navbar Component
const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <nav className="bg-white shadow-sm px-4 py-4 lg:px-8 relative z-40">
      <div className="flex justify-between items-center max-w-7xl mx-auto">
        {/* Logo */}
        <div className="flex items-center">
          <Link to="/" className="cursor-pointer">
            <img 
              src="/assets/landingpage/logo.png" 
              alt="TrendVector Logo" 
              className="w-auto hover:opacity-80 transition-opacity"
              style={{ height: '60px' }}
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          <Link to="/" className="text-gray-700 hover:text-blue-600 font-medium transition-colors font-sans">Home</Link>
          <Link to="/about" className="text-gray-700 hover:text-blue-600 font-medium transition-colors font-sans">About</Link>
          <Link to="/pricing" className="text-gray-700 hover:text-blue-600 font-medium transition-colors font-sans">Pricing</Link>
          <Link to="/contact" className="text-gray-700 hover:text-blue-600 font-medium transition-colors font-sans">Contact</Link>
        </div>

        {/* Desktop CTA Buttons */}
        <div className="hidden md:flex items-center space-x-4">
          <Link to="/login" className="text-gray-700 hover:text-blue-600 font-medium transition-colors font-sans">
            Login
          </Link>
          <Link 
            to="/signup" 
            className="bg-black hover:bg-gray-800 text-white px-6 py-2 rounded-lg font-medium transition-colors font-sans"
          >
            Start for Free
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-gray-700 hover:text-blue-600 transition-colors"
          >
            {isMenuOpen ? <HiOutlineXMark className="w-6 h-6 text-gray-700" /> : <HiOutlineBars3 className="w-6 h-6 text-gray-700" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t">
          <div className="px-4 py-4 space-y-4">
            <Link to="/" className="block text-gray-700 hover:text-blue-600 font-medium font-sans">Home</Link>
            <Link to="/about" className="block text-gray-700 hover:text-blue-600 font-medium font-sans">About</Link>
            <Link to="/pricing" className="block text-gray-700 hover:text-blue-600 font-medium font-sans">Pricing</Link>
            <Link to="/contact" className="block text-gray-700 hover:text-blue-600 font-medium font-sans">Contact</Link>
            <div className="pt-4 border-t">
              <Link to="/login" className="block text-gray-700 hover:text-blue-600 font-medium mb-2 font-sans">Login</Link>
              <Link 
                to="/signup" 
                className="block bg-black hover:bg-gray-800 text-white px-6 py-2 rounded-lg font-medium text-center transition-colors font-sans"
              >
                Start for Free
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section Component
const HeroSection = () => {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-green-50 via-cyan-50 to-blue-50 py-20 lg:py-32">
      {/* Background Image Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: 'url(/assets/landingpage/background_bg.png)' }}
      ></div>
      
      {/* Content Container with relative positioning */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8 text-center">
        {/* Main Headline */}
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight font-heading">
          The Signal in The{' '}
          <span className="bg-gradient-to-r from-green-600 to-cyan-600 bg-clip-text text-transparent">
            Noise
          </span>
        </h1>

        {/* Sub-headline */}
        <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed font-sans">
          Unlock profitable trading opportunities with our dual-model algorithmic analysis. 
          Get unbiased insights from thousands of stocks in minutes.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <Link 
            to="/signup" 
            className="bg-black hover:bg-gray-800 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg font-sans"
          >
            Start Free Trial
          </Link>
          <Link 
            to="/contact" 
            className="border-2 border-gray-300 hover:border-gray-400 text-gray-700 hover:text-gray-800 px-8 py-4 rounded-lg font-bold text-lg transition-colors font-sans"
          >
            Get a Demo
          </Link>
        </div>

        {/* Social Proof Logos */}
        <div className="mt-16">
          <p className="text-gray-500 text-sm mb-8 font-sans">Trusted by leading financial institutions</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 opacity-60">
            <div className="text-gray-400 font-bold text-xl font-sans">Bloomberg</div>
            <div className="text-gray-400 font-bold text-xl font-sans">Reuters</div>
            <div className="text-gray-400 font-bold text-xl font-sans">Forbes</div>
            <div className="text-gray-400 font-bold text-xl font-sans">CNBC</div>
            <div className="text-gray-400 font-bold text-xl font-sans">MarketWatch</div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Results Section Component
const ResultsSection = () => {
  const [activeFeature, setActiveFeature] = useState('performance');
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Feature data with corresponding images and content
  const features = [
    {
      id: 'performance',
      title: 'Signal Performance',
      description: 'Track the accuracy and performance of our algorithmic signals over time.',
      icon: <HiOutlineChartBar className="w-8 h-8 text-white" />,
      iconBg: 'bg-gradient-to-br from-green-500 to-green-600',
      image: '/assets/landingpage/chart_image.png',
      dots: ['bg-green-400', 'bg-cyan-400']
    },
    {
      id: 'accuracy',
      title: '95% Signal Accuracy',
      description: 'Our dual-model approach ensures high-precision signals with minimal false positives, giving you confidence in your trading decisions.',
      icon: <HiOutlineCheckCircle className="w-8 h-8 text-white" />,
      iconBg: 'bg-gradient-to-br from-green-500 to-green-600',
      image: '/assets/landingpage/95_percent.png', // Using same image for now
      dots: ['bg-green-400', 'bg-green-500']
    },
    {
      id: 'realtime',
      title: 'Real-time Analysis',
      description: 'Get instant insights on thousands of stocks with our lightning-fast algorithmic scanning that processes market data in milliseconds.',
      icon: <HiOutlineChartBar className="w-8 h-8 text-white" />,
      iconBg: 'bg-gradient-to-br from-cyan-500 to-cyan-600',
      image: '/assets/landingpage/real_time.png', // Using same image for now
      dots: ['bg-cyan-400', 'bg-blue-400']
    },
    {
      id: 'unbiased',
      title: 'Unbiased Insights',
      description: 'Remove emotional bias from your trading with our purely data-driven approach that focuses on market signals and technical analysis.',
      icon: <HiOutlineCpuChip className="w-8 h-8 text-white" />,
      iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600',
      image: '/assets/landingpage/unbiased.png', // Using same image for now
      dots: ['bg-blue-400', 'bg-purple-400']
    }
  ];

  const currentFeature = features.find(f => f.id === activeFeature);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const chartVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.section 
      ref={ref}
      className="bg-gray-900 text-white py-20 lg:py-32"
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Headline */}
        <motion.div 
          className="text-center mb-16"
          variants={itemVariants}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading">
            Unbiased Insights,{' '}
            <span className="bg-gradient-to-r from-green-600 to-cyan-600 bg-clip-text text-transparent">Tangible Results</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto font-sans">
            Our algorithmic analysis delivers clear, data-driven signals that help you make informed trading decisions.
          </p>
        </motion.div>

        {/* Chart and Features Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Interactive Chart/Image Container */}
          <motion.div 
            className="relative"
            variants={chartVariants}
          >
            <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold font-heading">{currentFeature.title}</h3>
                <div className="flex space-x-2">
                  {currentFeature.dots.map((dotClass, index) => (
                    <div key={index} className={`w-3 h-3 ${dotClass} rounded-full`}></div>
                  ))}
                </div>
              </div>
              
                             {/* Animated Image Container */}
               <div className="relative bg-gray-900 rounded overflow-hidden" style={{ height: 'calc(100% - 100px)' }}>
                 <AnimatePresence mode="wait">
                   <motion.div
                     key={activeFeature}
                     className="w-full h-full flex items-center justify-center"
                     initial={{ opacity: 0, scale: 1.1 }}
                     animate={{ opacity: 1, scale: 1 }}
                     exit={{ opacity: 0, scale: 0.9 }}
                     transition={{ duration: 0.5, ease: "easeInOut" }}
                   >
                     <img 
                       src={currentFeature.image} 
                       alt={`${currentFeature.title} Chart`} 
                       className="w-full h-full object-cover"
                       onError={(e) => {
                         e.target.style.display = 'none';
                         e.target.nextSibling.style.display = 'flex';
                       }}
                     />
                     {/* Fallback content when image fails to load */}
                     <div 
                       className="hidden w-full h-full items-center justify-center text-center p-8"
                       style={{ display: 'none' }}
                     >
                       <div>
                         <div className={`w-16 h-16 ${currentFeature.iconBg} rounded-lg flex items-center justify-center mx-auto mb-4`}>
                           {currentFeature.icon}
                         </div>
                         <h4 className="text-xl font-semibold mb-2">{currentFeature.title}</h4>
                         <p className="text-gray-400 text-sm">{currentFeature.description}</p>
                       </div>
                     </div>
                   </motion.div>
                 </AnimatePresence>
               </div>
            </div>
          </motion.div>

          {/* Interactive Feature Callouts */}
          <motion.div 
            className="space-y-8"
            variants={containerVariants}
          >
            {features.slice(1).map((feature, index) => (
              <motion.div
                key={feature.id}
                variants={itemVariants}
                className={`transition-all duration-300 cursor-pointer group ${
                  activeFeature === feature.id 
                    ? 'transform scale-105' 
                    : 'hover:transform hover:scale-102'
                }`}
              >
                <button
                  onClick={() => setActiveFeature(feature.id)}
                  className="w-full text-left"
                >
                  <div className={`flex items-start space-x-4 p-4 rounded-lg transition-all duration-300 ${
                    activeFeature === feature.id 
                      ? 'bg-gray-800 border border-gray-600' 
                      : 'hover:bg-gray-800/50'
                  }`}>
                    <div className={`flex-shrink-0 w-12 h-12 ${feature.iconBg} rounded-lg flex items-center justify-center transition-all duration-300 ${
                      activeFeature === feature.id 
                        ? 'scale-110 shadow-lg' 
                        : 'group-hover:scale-105'
                    }`}>
                      {feature.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-xl font-semibold mb-2 font-heading transition-colors duration-300 ${
                        activeFeature === feature.id 
                          ? 'text-white' 
                          : 'text-gray-300 group-hover:text-white'
                      }`}>
                        {feature.title}
                      </h3>
                      <p className={`font-sans transition-colors duration-300 ${
                        activeFeature === feature.id 
                          ? 'text-gray-200' 
                          : 'text-gray-400 group-hover:text-gray-300'
                      }`}>
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </button>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

// How It Works Section Component
const HowItWorksSection = () => {
  const steps = [
    {
      icon: <HiOutlineMagnifyingGlass className="w-12 h-12 text-gray-700" />,
      title: "1. Instant Scan",
      description: "Our algorithm scans thousands of stocks in real-time, identifying potential opportunities based on multiple technical indicators and market patterns."
    },
    {
      icon: <HiOutlineCpuChip className="w-12 h-12 text-gray-700" />,
      title: "2. Algorithmic Insight",
      description: "Dual-model analysis provides unbiased insights, combining short-term momentum signals with long-term trend analysis for comprehensive market understanding."
    },
    {
      icon: <HiOutlineChartBar className="w-12 h-12 text-gray-700" />,
      title: "3. Final Call",
      description: "You make the final decision with clear, data-driven recommendations that highlight entry points, exit strategies, and risk management."
    }
  ];

  return (
    <section className="bg-white py-20 lg:py-32 relative z-10">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Headline */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-heading">
            How <span className="bg-gradient-to-r from-green-600 to-cyan-600 bg-clip-text text-transparent">
            TrendVector
          </span> Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-sans">
            Our three-step process transforms complex market data into actionable trading insights.
          </p>
        </div>

                 {/* Three Column Grid */}
         <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
           {steps.map((step, index) => (
             <div key={index} className="text-center group">
                               <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  {step.icon}
                </div>
               <h3 className="text-2xl font-bold text-gray-900 mb-4 font-heading">{step.title}</h3>
               <p className="text-gray-600 leading-relaxed font-sans">{step.description}</p>
             </div>
           ))}
         </div>
      </div>
    </section>
  );
};

// Testimonial Section Component - Now using TestimonialSwiper
const TestimonialSection = () => {
  return <TestimonialSwiper />;
};

// Pricing Section Component
const PricingSectionComponent = () => {
  return <PricingSection />;
};

// Final CTA Section Component
const FinalCTASection = () => {
  return (
    <section className="bg-gradient-to-r from-green-600 to-cyan-600 py-20 lg:py-32">
      <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center text-white">
        <h2 className="text-4xl md:text-5xl font-bold mb-6 font-heading">
          Ready to Transform Your Trading?
        </h2>
        <p className="text-xl mb-8 opacity-90 font-sans">
          Join thousands of traders who are already making data-driven decisions with TrendVector.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/signup" 
            className="bg-white text-green-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg flex items-center justify-center space-x-2 font-sans"
          >
            <span>Start Free Trial</span>
            <HiOutlineArrowRight className="w-5 h-5" />
          </Link>
          <Link 
            to="/pricing" 
            className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-4 rounded-lg font-bold text-lg transition-colors font-sans"
          >
            View Pricing
          </Link>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div className="md:col-span-2">
            <img 
              src="/assets/landingpage/trendvector_logo_white.svg" 
              alt="TrendVector Logo" 
              className="w-auto mb-4"
              style={{ height: '50px' }}
            />
            <p className="text-gray-300 mb-4 max-w-md font-sans">
              Empowering traders with data-driven insights through advanced algorithmic analysis 
              and unbiased market signals.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4 font-heading">Quick Links</h3>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors font-sans">About Us</Link></li>
              <li><Link to="/pricing" className="text-gray-300 hover:text-white transition-colors font-sans">Pricing</Link></li>
              <li><Link to="/contact" className="text-gray-300 hover:text-white transition-colors font-sans">Contact</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold mb-4 font-heading">Legal</h3>
                         <ul className="space-y-2">
                               <li><Link to="/terms" className="text-gray-300 hover:text-white transition-colors font-sans">Terms & Conditions</Link></li>
                               <li><Link to="/privacy" className="text-gray-300 hover:text-white transition-colors font-sans">Privacy Policy</Link></li>
                               <li><Link to="/refund" className="text-gray-300 hover:text-white transition-colors font-sans">Refund Policy</Link></li>
             </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
          <div className="text-gray-300 text-center md:text-left mb-4 md:mb-0 font-sans">
            Made with ❤️ in India | Copyright © 2025, TrendVector
          </div>
                     <div className="flex space-x-6">
             <a href="https://www.facebook.com/profile.php?id=61580961714152" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors font-sans">Facebook</a>
             <a href="https://www.instagram.com/trend.vector/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors font-sans">Instagram</a>
             <a href="https://www.youtube.com/@TrendVector-z1b" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors font-sans">YouTube</a>
           </div>
        </div>
      </div>
    </footer>
  );
};

// Main NewLandingPage Component
const NewLandingPage = () => {
  useEffect(() => {
    // Log landing page load for debugging
    console.log('🏠 Landing page loaded successfully');
    
    // Clear any navigation state that might cause unwanted redirects
    // This ensures the landing page stays mounted
    window.history.replaceState(null, '', '/');
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <HeroSection />
        <ResultsSection />
        <StickyParallaxSection />
        <HowItWorksSection />
        <FeaturesSection />
        <TestimonialSection />
        <PricingSectionComponent />
        <FinalCTASection />
      </main>
      <Footer />
    </div>
  );
};

export default NewLandingPage;
